import { useEffect, useRef, useState } from 'react';
import { CHECKOUT_TAB_STATES } from '../constants/bookingConst';
import { formatCountdown, msUntil } from '../utils/countdownUtil';
import '../css/PosCheckout.css';

/**
 * Card copy per checkout-tab state.
 * @param {string} submitLabel - the checkout page's final button, quoted in the text.
 * @returns {Object<string, {text: string, primary: string}>}
 */
const buildCopy = (submitLabel) => ({
  [CHECKOUT_TAB_STATES.BLOCKED]: {
    text: 'Your browser didn’t open the checkout page automatically. Tap the button below to open it in a new tab.',
    primary: 'Open checkout',
  },
  [CHECKOUT_TAB_STATES.CLOSED]: {
    text: 'The checkout tab was closed. Nothing has been sent yet — reopen it to finish your payment.',
    primary: 'Reopen checkout',
  },
  default: {
    text: `Your checkout is open in a new tab. Pay and upload your receipt there, then tap “${submitLabel}” — you’ll be brought back here automatically.`,
    primary: 'Go to checkout tab',
  },
});

/**
 * What the opening screen shows while checkout is open in its own tab — the
 * court-booking system's CheckoutTabPending: jump back to (or reopen) that
 * tab, or back out. Sits inside the dialog the checkout was opened from.
 *
 * @param {object} props
 * @param {string} props.tabState - CHECKOUT_TAB_STATES value.
 * @param {() => void} props.onOpenCheckout - focus / reopen the checkout tab (runs in the click, so window.open is allowed).
 * @param {() => void} props.onBack - the secondary button: back out of checkout.
 * @param {string} props.submitLabel - name of the checkout page's final button.
 * @param {string} [props.backLabel] - label of the secondary button.
 * @param {number|null} [props.deadline] - epoch ms the payment window closes; null for none
 * @param {() => void} [props.onExpire] - the deadline passed; called once.
 * @param {string|null} [props.heading] - null when the dialog's own title already says it
 * @returns {JSX.Element}
 */
function PosCheckoutTabPending({
  tabState, onOpenCheckout, onBack, submitLabel, backLabel = 'Back', deadline = null, onExpire,
  heading = 'Complete your payment',
}) {
  const [msLeft, setMsLeft] = useState(() => msUntil(deadline));
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  // Same clock as the checkout tab.
  useEffect(() => {
    if (!deadline) return undefined;
    const tick = () => {
      const remaining = msUntil(deadline);
      setMsLeft(remaining);
      if (remaining !== null && remaining <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpireRef.current?.();
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  const copyByState = buildCopy(submitLabel);
  const copy = copyByState[tabState] || copyByState.default;

  return (
    <div className="ps-ctp">
      {heading && <h4 className="ps-ctp-title">{heading}</h4>}
      <p className="ps-ctp-text">{copy.text}</p>

      {msLeft !== null && (
        <div className="ps-ctp-timer">
          Complete payment within <strong>{formatCountdown(msLeft)}</strong> or this request expires.
        </div>
      )}

      <div className="ps-ctp-actions">
        <button type="button" className="ps-ctp-btn ps-ctp-btn-primary" onClick={onOpenCheckout}>
          {copy.primary}
        </button>
        <button type="button" className="ps-ctp-btn ps-ctp-btn-secondary" onClick={onBack}>
          {backLabel}
        </button>
      </div>
    </div>
  );
}

export default PosCheckoutTabPending;
